import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Box,
  TextField,
  Checkbox,
  Button,
} from "@mui/material";
import { useEffect, useState } from "react";
import EllipsisVerticalIcon from "assets/icons/EllipsisVertical.svg";
import ArrowUp from "assets/icons/ArrowUp.svg";
import Calendar from "assets/icons/calendar-svgrepo-com.svg";
import { useRTL } from "hooks/isRTL";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { fallbackValue } from "constants/fallbackConstant";

const PaginatedTable = ({
  columns, // the tabel head col
  rows, // data rows to display
  isLoading, // flag to show loading state
  rowsPerPageOptions = [10, 25, 100], // Pagination options
  onRowClick, // Click handler for rows
  actions, // Array of actions for row menu
  page, // Current page number
  rowsPerPage, // Rows per page
  handleChangePage, // Function to handle page change
  handleChangeRowsPerPage, // Function to handle rows per page change
  totalRecords, // Total number of records
  showActions = true, // Flag to toggle action menu visibility
  customColumnStyles = {}, // Custom column styles for dynamic width
  onSortChange, // for for sorting behavior
  onSearch,
  onRegistrationStatusChange,
  statusOptions = [],
  filters = {},
  ordrStatusOptions = [],
  handleStatusChange,
  handleDateChange,
}) => {
  const [searchValues, setSearchValues] = useState(filters);

  const { t, isRTL } = useRTL();

  const [menuState, setMenuState] = useState({
    menuType: null,
    anchorEl: null,
    currentColumn: null,
    selectedRow: null,
  });

  const handleMenuOpen = (menuType, event, options = {}) => {
    event.stopPropagation();
    setMenuState({
      menuType,
      anchorEl: event.currentTarget,
      ...options,
    });
  };

  const handleMenuClose = () => {
    setMenuState({
      menuType: null,
      anchorEl: null,
      currentColumn: null,
      selectedRow: null,
    });
  };

  const handleSortingMenuOpen = (event, columnId) => {
    handleMenuOpen("sorting", event, { currentColumn: columnId });
  };

  const handleActionMenuOpen = (event, row) => {
    handleMenuOpen("action", event, { selectedRow: row });
  };

  const handleStatusMenuOpen = (event, columnName) => {
    handleMenuOpen("status", event, { currentColumn: columnName });
  };

  const handlePickUpMenuOpen = (event) => {
    handleMenuOpen("pickUp", event);
  };

  const handleDropOffMenuOpen = (event) => {
    handleMenuOpen("dropOff", event);
  };

  useEffect(() => {
    setSearchValues(filters);
  }, [filters]);

  const handleSearchChange = (value, columnId) => {
    setSearchValues((prev) => ({
      ...prev,
      [columnId]: value,
    }));
    onSearch(value, columnId);
  };

  const getSortIcon = (columnId, columnName) => {
    if (
      columnId === "orderNumber" ||
      columnId === "senderName" ||
      columnId === "receiverName" ||
      columnId === "driverName" ||
      columnId === "pickUpLocation" ||
      columnId === "dropOffLocation"
    ) {
      return (
        <IconButton onClick={(event) => handleSortingMenuOpen(event, columnId)}>
          <img
            src={ArrowUp}
            alt="Arrow"
            style={{ width: "16px", height: "16px", filter: "invert(100%)" }}
          />
        </IconButton>
      );
    }
    if (columnId === "pickupDateTime") {
      return (
        <IconButton onClick={handlePickUpMenuOpen}>
          <img
            src={Calendar}
            alt="Arrow"
            style={{ width: "16px", height: "16px" }}
          />
        </IconButton>
      );
    }
    if (columnId === "dropOffDateTime") {
      return (
        <IconButton onClick={handleDropOffMenuOpen}>
          <img
            src={Calendar}
            alt="Arrow"
            style={{ width: "16px", height: "16px" }}
          />
        </IconButton>
      );
    }

    if (columnId === "creationDate" || columnId === "modificationDate") {
      return (
        <IconButton onClick={(event) => handleSortingMenuOpen(event, columnId)}>
          <img
            src={ArrowUp}
            alt="Arrow"
            style={{ width: "16px", height: "16px", filter: "invert(100%)" }}
          />
        </IconButton>
      );
    }

    if (columnId === "status") {
      return (
        // for the status drop down menu
        <IconButton
          onClick={(event) => handleStatusMenuOpen(event, columnName)}
        >
          <img
            src={ArrowUp}
            alt="Arrow"
            style={{ width: "16px", height: "16px", filter: "invert(100%)" }}
          />
        </IconButton>
      );
    }

    if (columnId === "phoneNumber" || columnId === "city") {
      // for Phone Number, only the search bar
      return (
        <IconButton onClick={(event) => handleSortingMenuOpen(event, columnId)}>
          <img
            src={ArrowUp}
            alt="Arrow"
            style={{ width: "16px", height: "16px", filter: "invert(100%)" }}
          />
        </IconButton>
      );
    }

    if (columnId === "fullName" || columnId === "email") {
      // shared behavior for sortable columns
      return (
        <IconButton onClick={(event) => handleSortingMenuOpen(event, columnId)}>
          <img
            src={ArrowUp}
            alt="Arrow"
            style={{ width: "16px", height: "16px", filter: "invert(100%)" }}
          />
        </IconButton>
      );
    }
    if (columnId === "age") {
      return (
        <IconButton onClick={(event) => handleSortingMenuOpen(event, columnId)}>
          <img
            src={ArrowUp}
            alt="Arrow"
            style={{ width: "16px", height: "16px", filter: "invert(100%)" }}
          />
        </IconButton>
      );
    }

    return null;
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer
        sx={{
          borderRadius: "8px",
          border: "1px solid #ddd",
          maxHeight: "500px",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  sx={{
                    width: column.minWidth || "150px",
                    backgroundColor: (theme) => theme.palette.text.primary,
                    color: (theme) => theme.palette.background.default,
                    paddingLeft: isRTL
                      ? index === columns.length - 1
                        ? "16px"
                        : "8px"
                      : index === 0
                      ? "24px"
                      : "8px",
                    paddingRight: isRTL
                      ? index === 0
                        ? "24px"
                        : "8px"
                      : index === columns.length - 1
                      ? "16px"
                      : "8px",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    textAlign:
                      customColumnStyles[column.id]?.textAlign ||
                      (isRTL ? "right" : "left"),
                    whiteSpace: "nowrap",
                  }}
                >
                  {column.label}
                  {column.sortable && getSortIcon(column.id, column.name)}{" "}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              backgroundColor: "background.white",
            }}
          >
            {isLoading
              ? Array.from({ length: rowsPerPage }).map((_, index) => (
                  <TableRow key={`loading-${index}`}>
                    {columns.map((column) => (
                      <TableCell key={column.id}>
                        <Skeleton variant="text" width="150px" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => onRowClick && onRowClick(row)}
                    sx={{
                      cursor: onRowClick ? "pointer" : "default",
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    {columns.map((column, index) => (
                      <TableCell
                        key={column.id}
                        sx={{
                          color: "text.dark",
                          paddingLeft: isRTL
                            ? index === columns.length - 1
                              ? "24px"
                              : "8px"
                            : index === 0
                            ? "24px"
                            : "8px",
                          paddingRight: isRTL
                            ? index === 0
                              ? "24px"
                              : "8px"
                            : index === columns.length - 1
                            ? "24px"
                            : "8px",
                          fontSize: "0.9rem",
                          textAlign:
                            customColumnStyles[column.id]?.textAlign ||
                            (isRTL ? "right" : "left"),
                          whiteSpace: "nowrap",
                          position: "relative",
                        }}
                      >
                        {column.id === "action" && showActions ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: isRTL ? "flex-start" : "flex-end",
                              alignItems: "center",
                              position: "absolute",
                              top: "50%",
                              [isRTL ? "left" : "right"]: "8px",
                              transform: "translateY(-50%)",
                            }}
                          >
                            <IconButton
                              onClick={(event) =>
                                handleActionMenuOpen(event, row)
                              }
                              sx={{
                                padding: 0,
                                margin: 0,
                              }}
                            >
                              <img
                                src={EllipsisVerticalIcon}
                                alt="Menu"
                                style={{ width: "30px", height: "30px" }}
                              />
                            </IconButton>
                          </Box>
                        ) : column.renderCell ? (
                          column.renderCell({
                            ...row,
                            stopPropagation: (e) => e.stopPropagation(), // Pass stopPropagation to renderCell
                          })
                        ) : (
                          row[column.id] || fallbackValue
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        backIconButtonProps={{
          sx: { transform: isRTL ? "rotate(180deg)" : "none" },
        }}
        nextIconButtonProps={{
          sx: { transform: isRTL ? "rotate(180deg)" : "none" },
        }}
      />
      <Menu
        anchorEl={menuState.anchorEl}
        open={Boolean(menuState.anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            style: {
              maxHeight: 360,
              overflowY: "auto",
            },
          },
        }}
      >
        {menuState.menuType === "sorting" && (
          <>
            {menuState.currentColumn === "email" ||
            menuState.currentColumn === "fullName" ||
            menuState.currentColumn === "age" ||
            menuState.currentColumn === "creationDate" ||
            menuState.currentColumn === "modificationDate" ? (
              <>
                <MenuItem
                  onClick={() => {
                    onSortChange(menuState.currentColumn, false);
                    handleMenuClose();
                  }}
                >
                  {menuState.currentColumn === "age" ||
                  menuState.currentColumn === "creationDate" ||
                  menuState.currentColumn === "modificationDate"
                    ? t("sortAscending")
                    : t("sortAZ")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    onSortChange(menuState.currentColumn, true);
                    handleMenuClose();
                  }}
                >
                  {menuState.currentColumn === "age" ||
                  menuState.currentColumn === "creationDate" ||
                  menuState.currentColumn === "modificationDate"
                    ? t("sortDescending")
                    : t("sortZA")}
                </MenuItem>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    padding: "8px 16px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder={
                      `${t("search")} ${
                        columns.find(
                          (col) => col.id === menuState.currentColumn
                        )?.placeholder ?? t(menuState.currentColumn)
                      }` || ""
                    }
                    value={searchValues[menuState.currentColumn] || ""}
                    onChange={(e) =>
                      handleSearchChange(
                        e.target.value,
                        menuState.currentColumn
                      )
                    }
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: 1,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ddd",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#bbb",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3f51b5", // soft primary
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </>
        )}

        {menuState.menuType === "pickUp" && (
          <Box sx={{ p: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={filters.pickupDate}
                onChange={(date) => {
                  handleDateChange("pickupDate", date);
                  handleMenuClose();
                }}
              />
              <Button
                variant="text"
                color="primary"
                onClick={() => {
                  handleDateChange("pickupDate", null); // Clear date
                  handleMenuClose();
                }}
              >
                Reset
              </Button>
            </LocalizationProvider>
          </Box>
        )}

        {menuState.menuType === "dropOff" && (
          <Box sx={{ p: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={filters.dropoffDate}
                onChange={(date) => {
                  handleDateChange("dropoffDate", date);
                  handleMenuClose();
                }}
              />
              <Button
                variant="text"
                color="primary"
                onClick={() => {
                  handleDateChange("dropoffDate", null); // Clear date
                  handleMenuClose();
                }}
              >
                Reset
              </Button>
            </LocalizationProvider>
          </Box>
        )}

        {/* this remain the same is it the one i should change make sure it do exisit? */}
        {menuState.menuType === "action" && (
          <>
            {(typeof actions === "function"
              ? actions(menuState.selectedRow)
              : actions
            )?.map((action) => (
              <MenuItem
                key={action.label}
                onClick={() => {
                  action.onClick(menuState.selectedRow);
                  handleMenuClose();
                }}
              >
                {action.label}
              </MenuItem>
            ))}
          </>
        )}

        {menuState.menuType === "status" && (
          <>
            {menuState.currentColumn === "orderStatus" ? (
              ordrStatusOptions.map((status) => (
                <MenuItem
                  key={status.value}
                  onClick={() => {
                    handleStatusChange(status.value);
                    handleMenuClose();
                  }}
                >
                  {status.label}
                </MenuItem>
              ))
            ) : menuState.currentColumn === "driverRequestStatus" ? (
              statusOptions.map((status) => (
                <MenuItem
                  key={status.value}
                  onClick={() => {
                    onRegistrationStatusChange([status.value]);
                    handleMenuClose();
                  }}
                  selected={filters?.status?.[0] === status.value}
                >
                  {status.label}
                </MenuItem>
              ))
            ) : (
              <>
                {/* Status Menu */}
                {statusOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    sx={{ display: "flex", alignItems: "center" }}
                    onClick={() => {
                      const isSelected = filters?.status?.includes(
                        option.value
                      );
                      const updatedStatuses = isSelected
                        ? filters?.status?.filter(
                            (status) => status !== option.value
                          )
                        : [...(filters?.status || []), option.value];

                      onRegistrationStatusChange(updatedStatuses);
                    }}
                  >
                    <Checkbox
                      checked={filters?.status?.includes(option.value)}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </>
            )}
          </>
        )}
      </Menu>
    </Paper>
  );
};

export default PaginatedTable;
